import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { api } from '../../App';
import { Copy, Check, Building, CreditCard, Hash, Globe, MapPin } from 'lucide-react';
import { toast } from 'sonner';

export default function PaymentInstructionsModal({ isOpen, onClose, commitment, onPaymentSent }) {
  const [instructions, setInstructions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [copiedField, setCopiedField] = useState(null);

  useEffect(() => {
    const loadInstructions = async () => {
      try {
        setLoading(true);
        const response = await api.get('/payment-instructions');
        setInstructions(response.data);
      } catch (error) {
        toast.error('Failed to load payment instructions');
        onClose();
      } finally {
        setLoading(false);
      }
    };

    if (isOpen) {
      loadInstructions();
    }
  }, [isOpen, onClose]);

  const handlePaymentSent = async () => {
    try {
      setSubmitting(true);
      await api.post(`/commitments/${commitment.commitment_id}/payment-sent`);
      toast.success("Payment marked as sent! Admin notified.");
      if (onPaymentSent) onPaymentSent();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to mark payment as sent");
    } finally {
      setSubmitting(false);
    }
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    toast.success(`${field} copied to clipboard`);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const InstructionRow = ({ icon: Icon, label, value }) => (
    <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg border border-white/5 hover:border-[#D4AF37]/30 transition-colors">
      <div className="flex items-center gap-3">
        <div className="p-2 bg-[#D4AF37]/10 rounded-lg">
          <Icon className="w-5 h-5 text-[#D4AF37]" />
        </div>
        <div>
          <p className="text-xs text-[#94A3B8] uppercase tracking-wider">{label}</p>
          <p className="font-['JetBrains_Mono'] text-white font-medium">{value}</p>
        </div>
      </div>
      <button 
        onClick={() => copyToClipboard(value, label)}
        className="p-2 hover:bg-white/10 rounded-lg transition-colors text-[#94A3B8] hover:text-white"
        title="Copy to clipboard"
      >
        {copiedField === label ? <Check className="w-4 h-4 text-[#10B981]" /> : <Copy className="w-4 h-4" />}
      </button>
    </div>
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-[#0F172A] border-white/10 text-white max-w-lg max-h-[85vh] p-0 flex flex-col gap-0">
        <div className="p-6 pb-4 border-b border-white/5">
          <DialogHeader className="p-0 space-y-0">
            <DialogTitle className="font-['Outfit'] text-2xl font-bold flex items-center gap-2">
              <CreditCard className="w-6 h-6 text-[#D4AF37]" />
              Payment Instructions
            </DialogTitle>
            <p className="text-[#94A3B8] text-sm mt-1">
              Please transfer the exact amount to complete your investment.
            </p>
          </DialogHeader>
        </div>

        {loading ? (
          <div className="py-12 flex justify-center flex-1">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#D4AF37]"></div>
          </div>
        ) : instructions && (
          <>
            <div className="flex-1 overflow-y-auto p-6 custom-scrollbar space-y-6">
              {/* Amount Highlight */}
              <div className="bg-gradient-to-r from-[#D4AF37]/20 to-transparent p-4 rounded-xl border border-[#D4AF37]/20">
                <p className="text-[#D4AF37] text-sm font-bold uppercase tracking-wider mb-1">Total Amount Due</p>
                <p className="font-['JetBrains_Mono'] text-3xl font-bold text-white">${commitment?.amount?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
              </div>

              <div className="space-y-2">
                <InstructionRow icon={Building} label="Bank Name" value={instructions.bank_name} />
                <InstructionRow icon={CreditCard} label="Beneficiary Name" value={instructions.account_name} />
                <InstructionRow icon={Hash} label="Account Number" value={instructions.account_number} />
                <InstructionRow icon={Globe} label="Routing / ABA" value={instructions.routing_number} />
                <InstructionRow icon={Globe} label="Swift / BIC" value={instructions.swift_code} />
                <InstructionRow icon={MapPin} label="Bank Address" value={instructions.bank_address} />
              </div>

              <div className="bg-blue-500/10 border border-blue-500/20 p-4 rounded-lg">
                <p className="text-blue-400 text-sm font-medium mb-1">Important Reference Note</p>
                <p className="text-[#94A3B8] text-xs leading-relaxed">
                  Please include your Commitment ID <span className="text-white font-mono bg-white/10 px-1 rounded">{commitment?.commitment_id}</span> in the wire transfer reference/memo field to ensure faster processing.
                </p>
              </div>
            </div>

            <div className="p-6 border-t border-white/5 flex gap-3 bg-[#0F172A]">
              <button onClick={onClose} className="flex-1 py-3 bg-white/5 hover:bg-white/10 rounded-lg transition-colors font-medium">
                Close
              </button>
              <button 
                onClick={handlePaymentSent} 
                disabled={submitting}
                className="flex-1 py-3 bg-[#D4AF37] text-[#0A1128] font-bold rounded-lg hover:shadow-[0_0_20px_rgba(212,175,55,0.4)] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 whitespace-nowrap"
              >
                {submitting ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#0A1128]"></div> : null}
                {submitting ? 'Processing...' : "I've Sent Payment"}
              </button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
